import {
  Tabs as AriaTabs,
  TabList,
  Tab,
  TabPanel,
  type TabsProps as AriaTabsProps,
  type TabProps,
  type TabListProps,
  type TabPanelProps,
} from 'react-aria-components'
import { cn } from '@/lib/utils'

// ============================================================================
// Types & Variants
// ============================================================================

export type TabsVariant = 'underline' | 'pills' | 'buttonGroup' | 'minimal'
export type TabsSize = 'sm' | 'md' | 'lg'

// ============================================================================
// Size Configurations
// ============================================================================

const sizeStyles: Record<TabsSize, { tab: string; icon: string; badge: string }> = {
  sm: {
    tab: 'px-3 py-1.5 text-xs gap-1.5',
    icon: 'h-3.5 w-3.5',
    badge: 'text-[10px] px-1.5 py-0.5 min-w-[18px]',
  },
  md: {
    tab: 'px-3 py-2 text-sm gap-2',
    icon: 'h-4 w-4',
    badge: 'text-xs px-2 py-0.5 min-w-[20px]',
  },
  lg: {
    tab: 'px-4 py-2.5 text-base gap-2.5',
    icon: 'h-5 w-5',
    badge: 'text-xs px-2.5 py-1 min-w-[24px]',
  },
}

// ============================================================================
// Variant Configurations
// ============================================================================

const listVariantStyles: Record<TabsVariant, string> = {
  underline: 'border-b border-gray-200 dark:border-gray-800',
  pills: 'gap-1 bg-gray-100 dark:bg-gray-800/80 p-1 rounded-lg',
  buttonGroup: 'gap-0 rounded-lg shadow-xs ring-1 ring-gray-300 dark:ring-gray-700',
  minimal: 'gap-1',
}

const tabVariantStyles: Record<
  TabsVariant,
  { base: string; selected: string; hover: string }
> = {
  underline: {
    base: cn(
      'relative -mb-px border-b-2 border-transparent',
      'text-gray-500 dark:text-gray-400',
      'transition-colors duration-150'
    ),
    selected: cn(
      'border-brand-500 dark:border-brand-400',
      'text-brand-600 dark:text-brand-400',
      'font-medium'
    ),
    hover: 'hover:text-gray-700 dark:hover:text-gray-300',
  },
  pills: {
    base: cn(
      'rounded-md',
      'text-gray-600 dark:text-gray-400',
      'transition-all duration-150'
    ),
    selected: cn(
      'bg-white dark:bg-gray-700',
      'text-gray-900 dark:text-gray-100',
      'shadow-sm',
      'font-medium'
    ),
    hover: 'hover:text-gray-900 dark:hover:text-gray-200',
  },
  buttonGroup: {
    base: cn(
      'bg-white dark:bg-gray-900',
      'text-gray-600 dark:text-gray-400',
      'border-r border-gray-300 dark:border-gray-700 last:border-r-0',
      'first:rounded-l-lg last:rounded-r-lg',
      'transition-colors duration-150'
    ),
    selected: cn(
      'bg-gray-50 dark:bg-gray-800',
      'text-gray-900 dark:text-gray-100',
      'font-medium'
    ),
    hover: 'hover:bg-gray-50 hover:text-gray-900 dark:hover:bg-gray-800/50 dark:hover:text-gray-200',
  },
  minimal: {
    base: cn(
      'rounded-md',
      'text-gray-500 dark:text-gray-400',
      'transition-colors duration-150'
    ),
    selected: cn(
      'text-brand-600 dark:text-brand-400',
      'font-medium'
    ),
    hover: 'hover:text-gray-700 dark:hover:text-gray-300',
  },
}

// ============================================================================
// Badge Styles
// ============================================================================

const badgeVariantStyles: Record<TabsVariant, { base: string; selected: string }> = {
  underline: {
    base: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    selected: 'bg-brand-100 text-brand-600 dark:bg-brand-900/50 dark:text-brand-400',
  },
  pills: {
    base: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
    selected: 'bg-brand-100 text-brand-600 dark:bg-brand-900/50 dark:text-brand-400',
  },
  buttonGroup: {
    base: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    selected: 'bg-brand-100 text-brand-600 dark:bg-brand-900/50 dark:text-brand-400',
  },
  minimal: {
    base: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    selected: 'bg-brand-100 text-brand-600 dark:bg-brand-900/50 dark:text-brand-400',
  },
}

// ============================================================================
// Tabs Root Component
// ============================================================================

export interface TabsProps extends AriaTabsProps {
  children: React.ReactNode
}

/**
 * Tabs - Untitled UI inspired tabs component
 *
 * A flexible tab navigation component with multiple visual variants and sizes.
 * Built on React Aria for full accessibility support.
 *
 * Variants:
 * - underline: Classic underline indicator (default, for page-level navigation)
 * - pills: Pill-shaped tabs with background highlight
 * - buttonGroup: Connected button group style with ring
 * - minimal: Minimal text-only tabs without background
 */
export function Tabs({ className, children, ...props }: TabsProps) {
  return (
    <AriaTabs className={cn('flex min-w-0 flex-col', className)} {...props}>
      {children}
    </AriaTabs>
  )
}

// ============================================================================
// Tabs List Component
// ============================================================================

export interface TabsListProps extends TabListProps<object> {
  children: React.ReactNode
  /** Visual variant */
  variant?: TabsVariant
  /** Size preset */
  size?: TabsSize
  /** Full width tabs (stretch to fill container) */
  fullWidth?: boolean
}

export function TabsList({
  className,
  children,
  variant = 'underline',
  size = 'md',
  fullWidth = false,
  ...props
}: TabsListProps) {
  return (
    <TabList
      className={cn(
        'flex overflow-x-auto scrollbar-hide',
        listVariantStyles[variant],
        fullWidth && 'w-full',
        className
      )}
      data-variant={variant}
      data-size={size}
      data-full-width={fullWidth}
      {...props}
    >
      {children}
    </TabList>
  )
}

// ============================================================================
// Tabs Trigger Component
// ============================================================================

export interface TabsTriggerProps extends TabProps {
  children: React.ReactNode
  /** Visual variant (inherits from TabsList if not specified) */
  variant?: TabsVariant
  /** Size preset (inherits from TabsList if not specified) */
  size?: TabsSize
  /** Icon to display before the label */
  icon?: React.ReactNode
  /** Badge count or content */
  badge?: number | string
  /** Make tab stretch to fill available space */
  grow?: boolean
}

export function TabsTrigger({
  className,
  children,
  variant,
  size,
  icon,
  badge,
  grow = false,
  ...props
}: TabsTriggerProps) {
  return (
    <Tab
      className={({ isSelected, isDisabled, isHovered, isFocusVisible }) => {
        // Get variant/size from parent TabsList data attributes if not specified
        const resolvedVariant = variant || 'underline'
        const resolvedSize = size || 'md'

        const variantStyles = tabVariantStyles[resolvedVariant]
        const sizes = sizeStyles[resolvedSize]
        const badgeStyles = badgeVariantStyles[resolvedVariant]

        return cn(
          // Base styles
          'inline-flex items-center justify-center whitespace-nowrap',
          'font-medium cursor-pointer outline-none',
          'flex-shrink-0',
          sizes.tab,
          variantStyles.base,

          // Hover state (only when not selected or disabled)
          !isSelected && !isDisabled && variantStyles.hover,

          // Selected state
          isSelected && variantStyles.selected,

          // Focus visible state
          isFocusVisible && cn(
            'ring-2 ring-brand-500 ring-offset-2',
            'dark:ring-brand-400 dark:ring-offset-gray-900',
            resolvedVariant === 'underline' && 'rounded-sm'
          ),

          // Disabled state
          isDisabled && 'opacity-50 cursor-not-allowed',

          // Grow to fill space
          grow && 'flex-1',

          className
        )
      }}
      {...props}
    >
      {({ isSelected }) => {
        // Get variant/size from parent data attributes for badge styling
        const resolvedVariant = variant || 'underline'
        const resolvedSize = size || 'md'
        const sizes = sizeStyles[resolvedSize]
        const badgeStyles = badgeVariantStyles[resolvedVariant]

        return (
          <>
            {icon && (
              <span className={cn(sizes.icon, '[&>svg]:h-full [&>svg]:w-full')}>
                {icon}
              </span>
            )}
            <span>{children}</span>
            {badge !== undefined && (
              <span
                className={cn(
                  'rounded-full font-medium text-center',
                  sizes.badge,
                  isSelected ? badgeStyles.selected : badgeStyles.base
                )}
              >
                {badge}
              </span>
            )}
          </>
        )
      }}
    </Tab>
  )
}

// ============================================================================
// Tabs Content Component
// ============================================================================

export interface TabsContentProps extends TabPanelProps {
  children: React.ReactNode
}

export function TabsContent({ className, children, ...props }: TabsContentProps) {
  return (
    <TabPanel
      className={cn(
        'min-w-0 py-4 outline-none',
        'focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
        'dark:focus-visible:ring-brand-400 dark:focus-visible:ring-offset-gray-900',
        className
      )}
      {...props}
    >
      {children}
    </TabPanel>
  )
}

// ============================================================================
// Compound Component Exports
// ============================================================================

Tabs.List = TabsList
Tabs.Trigger = TabsTrigger
Tabs.Content = TabsContent
Tabs.Item = TabsTrigger // Alias for Item pattern

export default Tabs
